/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Typography, Box, alpha } from '@mui/material';
import { ArrowRightAltSharp } from '@mui/icons-material';
import moment from 'moment';
import modifyScriptName from '../../../../utils/modifyScriptName';
import { CloseTradeModalRefactored } from './index';
import PriceWithColor from '../watchlist/PriceWithColor';
import { fNumber } from '../../../../utils/formatNumber';
import CloseTradeModal from './CloseTradeModal';

const TradeCard = ({ trade, priceChangeConfig, closeFromTrade = false, closeFromPortfolio = false }) => {
  const { script, side, quantity, buy, sell, type, margin, boughtAt, soldAt } = trade;

  const [openModal, setOpenModal] = useState(false);
  let multiplicationFactor = 1;
  const currentPrice = script?.depth ? script.depth[side === 'BUY' ? 'buy' : 'sell'][0].price : 0;
  const currentSegement = trade?.script?.exchange?.name;
  if (priceChangeConfig && ['MCX', 'COMEX'].includes(currentSegement)) {
    multiplicationFactor = priceChangeConfig[currentSegement][trade.script.name];
  }
  // Calculate active profit/loss
  const calculateProfitLoss = () => {
    if (side === 'BUY') {
      return (currentPrice - buy) * quantity * multiplicationFactor;
    }
    return (sell - currentPrice) * quantity * multiplicationFactor;
  };

  const activeProfitLoss = calculateProfitLoss();

  const openModalHandler = () => setOpenModal(true);
  const closeModalHandler = () => {
    setOpenModal(false);
  };
  const formatDateTime = (date) => moment(date).format('DD MMM hh:mm:ss A');

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: (theme) => theme.palette.mode === 'light' ? 'grey.200' : 'grey.800',
          borderRadius: 1,
          mt: 1.2,
          // mb: 0.5,
          width: '100%',
          pointerEvents: openModal ? 'none' : 'auto',
          cursor: 'pointer',          transition: 'all 0.15s ease-in-out',
          bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
          ':hover': {
            borderColor: (theme) => theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
            bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
            transform: 'translateX(2px)',
          },
        }}
        onClick={closeFromTrade || closeFromPortfolio ? openModalHandler : undefined}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Main Content */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {/* Top Row: Symbol, Side, Quantity, and P/L */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: 2,
              mb: 0.75
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  {modifyScriptName(script.tradingsymbol, script.expiry)}
                </Typography>
                <Box
                  sx={{
                    px: 1,
                    height: '24px',
                    borderRadius: 1,
                    fontSize: '0.813rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    textTransform: 'uppercase',
                    display: 'inline-flex',
                    alignItems: 'center', color: side === 'BUY' ? 'success.main' : 'error.main',
                    bgcolor: side === 'BUY' ? 'success.lighter' : 'error.lighter',
                    flexShrink: 0,
                    letterSpacing: '0.02em',
                  }}
                >
                  {side.toUpperCase()} • {quantity}
                </Box>
              </Box>

              {/* Profit/Loss or Price */}
              <Box sx={{
                flexShrink: 0,
                px: 1.5,
                py: 0.5,
                borderRadius: 1, bgcolor: (theme) => {
                  if (type !== 'OPEN') {
                    return 'transparent';
                  }
                  return +activeProfitLoss < 0
                    ? alpha(theme.palette.error.main, 0.08)
                    : alpha(theme.palette.success.main, 0.08);
                }
              }}>
                {type === 'OPEN' ? (
                  <Typography
                    sx={{
                      color: +activeProfitLoss < 0 ? 'error.main' : 'success.main',
                      fontWeight: 700,
                      fontSize: '1rem',
                      lineHeight: 1,
                      letterSpacing: '0.02em',
                    }}
                  >
                    {fNumber(activeProfitLoss.toFixed(2))}
                  </Typography>
                ) : (
                  <PriceWithColor
                    price={script?.depth ? script?.depth[side === 'BUY' ? 'sell' : 'buy'][0].price.toFixed(2) : 0}
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Bottom Row: Prices and Margin */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: { xs: 1, sm: 1.5 },
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              flexWrap: { xs: 'wrap', sm: 'nowrap' }
            }}>
              <span>{side === 'BUY' ? fNumber(buy) : fNumber(sell)}</span>
              {type === 'OPEN' && (
                <>
                  <ArrowRightAltSharp sx={{ fontSize: 18, color: 'text.disabled' }} />
                  <span style={{ color: 'text.primary', fontWeight: 600 }}>{fNumber(currentPrice?.toFixed(2))}</span>
                </>
              )}
              <Box
                component="span"
                sx={{
                  ml: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.6rem',
                }}
              >
                {closeFromPortfolio === false && closeFromTrade === true ? (<span>{side === 'BUY' ? formatDateTime(boughtAt) : formatDateTime(soldAt)}</span>) : (<span>MARGIN: {fNumber(Number.parseInt(margin, 10))}</span>)}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>      
      <CloseTradeModal
        isOpen={openModal}
        onClose={closeModalHandler}
        trade={trade}
        updatedQuantity={quantity}
        closeFromTrade={closeFromTrade}
        closeFromPortfolio={closeFromPortfolio}
      />
    </>
  );
};

export default TradeCard;
